import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { parseLocalDropboxRoot } from "@shared/local-project-path";
import { apiRequest } from "@/lib/queryClient";
import { localDropboxPreferencesKey, useLocalDropboxRoot, type LocalDropboxPreferences } from "@/hooks/use-local-dropbox-root";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/** Mounted with the current account as a key by AccountSettingsDialog. */
export function LocalDropboxSettings({ userId, open }: { userId: number; open: boolean }) {
  const queryClient = useQueryClient();
  const preferences = useLocalDropboxRoot(userId, open);
  const [draft, setDraft] = useState("");
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);
  useEffect(() => {
    if (preferences.data && !dirty) {
      setDraft(preferences.data.localDropboxRootPath ?? "");
    }
  }, [preferences.data, dirty]);

  const save = useMutation({
    mutationFn: async (path: string | null) => {
      const response = await apiRequest("PATCH", "/api/user/preferences", { localDropboxRootPath: path });
      return await response.json() as LocalDropboxPreferences;
    },
    onSuccess: (data) => {
      if (!active.current) return;
      queryClient.setQueryData(localDropboxPreferencesKey(userId), data);
      setDraft(data.localDropboxRootPath ?? "");
      setDirty(false);
      setError(null);
      setNotice(data.localDropboxRootPath ? "Local Projects folder saved." : "Saved local Projects folder cleared.");
      // Existing preference consumers keep their own shapes and refresh normally.
      void queryClient.invalidateQueries({
        queryKey: ["/api/user/preferences"],
        // The local-root cache already has the authoritative save response.
        predicate: query => query.queryKey[1] !== "local-dropbox-root",
      });
    },
    onError: (failure) => {
      if (!active.current) return;
      setNotice(null);
      setError(failure instanceof Error ? failure.message : "Unable to save your local Projects folder.");
    },
  });
  const handleSave = () => {
    setError(null);
    setNotice(null);
    try {
      save.mutate(draft.trim() ? parseLocalDropboxRoot(draft).path : null);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Enter a full Mac or Windows folder path.");
    }
  };
  let format: string | null = null;
  try {
    if (draft.trim()) format = parseLocalDropboxRoot(draft).platform === "mac" ? "Mac (/)" : "Windows (\\)";
  } catch { /* Validation is reported on Save, not while typing. */ }
  const disabled = preferences.isPending || preferences.isError || save.isPending;
  return (
    <section aria-labelledby="local-dropbox-heading" className="space-y-4 rounded-lg border border-gray-600 bg-gray-900/25 p-4 sm:p-5">
      <div>
        <h3 id="local-dropbox-heading" className="font-medium text-gray-100">Local Dropbox Projects folder</h3>
        <p id="local-dropbox-help" className="mt-1 text-sm text-gray-400">
          Paste the full local path to the shared Projects folder on your computer—not the top-level Dropbox folder.
          This is used by Copy Local Path in a project&apos;s ID menu.
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="local-dropbox-root">Local Projects folder path</Label>
        <Input
          id="local-dropbox-root"
          value={draft}
          onChange={event => { setDraft(event.target.value); setDirty(true); setError(null); setNotice(null); }}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          aria-describedby="local-dropbox-help local-dropbox-examples local-dropbox-sync"
          aria-invalid={!!error}
          className="bg-gray-800 border-gray-600 text-gray-200 font-mono text-sm"
          placeholder="Paste the full path to your local Projects folder"
        />
        {format && <p className="text-xs text-gray-400">Detected path format: {format}</p>}
        <div id="local-dropbox-examples" className="space-y-1 text-xs text-gray-400 break-all">
          <p>Mac example: <code>/Users/alex/Library/CloudStorage/Dropbox-Team/ACOLORI/Projects</code></p>
          <p>Windows example: <code>D:\Dropbox\ACOLORI\Projects</code></p>
        </div>
      </div>
      <div id="local-dropbox-sync" className="space-y-2 text-xs leading-relaxed text-gray-400">
        <p>One default path is saved per account. If you switch computers, update it here. The path format—not your browser—determines Mac or Windows slashes.</p>
        <p>The folder must be included in Dropbox sync on this computer. Selective Sync can remove it locally; online-only folders remain visible but may need internet to download files. This website cannot check local availability, download the folder, or change sync settings.</p>
      </div>
      {preferences.isPending && <p role="status" className="text-sm text-gray-400">Loading your saved path…</p>}
      {preferences.isError && (
        <div role="alert" className="text-sm text-red-300">
          {preferences.error instanceof Error ? preferences.error.message : "Unable to load your saved path."}
          <Button type="button" variant="ghost" size="sm" onClick={() => void preferences.refetch()}>Retry</Button>
        </div>
      )}
      {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
      {notice && <p role="status" className="text-sm text-green-300">{notice}</p>}
      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={handleSave} disabled={disabled || !dirty}>
          {save.isPending ? "Saving…" : "Save path"}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={disabled || (!draft && !preferences.data?.localDropboxRootPath)}
          onClick={() => { setError(null); setNotice(null); save.mutate(null); }}
          className="border-gray-600 bg-transparent"
        >Clear saved path</Button>
      </div>
    </section>
  );
}